// @ts-nocheck
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Users, 
  DollarSign,
  Eye,
  Globe,
  Loader2,
  Settings,
  CreditCard,
  ListChecks,
  Info,
  MapPinned,
  FileText,
  Trophy,
  HelpCircle,
  UserCheck,
  Mail,
  CalendarDays,
  Plug,
  TrendingUp,
  ClipboardList
} from 'lucide-react';
import EventPricingConfig from './EventPricingConfig';
import EventRegistrationsAdmin from './EventRegistrationsAdmin';
import EventFinanceTab from '@/components/admin-dashboard/EventFinanceTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import AdminLayout from '@/layouts/AdminLayout';

// Import OLD system tabs (excluding obsolete Registration Pricing and Extras tabs)
import BasicInfoTab from '@/components/admin-dashboard/event-form/BasicInfoTab';
import VenueLocationTab from '@/components/admin-dashboard/event-form/VenueLocationTab';
import DetailsTab from '@/components/admin-dashboard/event-form/DetailsTab';
import SponsorsTab from '@/components/admin-dashboard/event-form/SponsorsTab';
import FAQsTab from '@/components/admin-dashboard/event-form/FAQsTab';
import CoachesTab from '@/components/admin-dashboard/event-form/CoachesTab';
import IntegrationsTab from '@/components/admin-dashboard/event-form/IntegrationsTab';
import { EventScheduleRequestsTab } from '@/components/admin/event-schedule-requests/EventScheduleRequestsTab';
import { EventFormData } from '@/components/admin-dashboard/CreateEventForm';
import { toast } from 'sonner';
import { parseLocalDate } from '@/utils/formatters';

function EventManagementContent() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EventFormData | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: event, isLoading, refetch } = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!eventId
  });

  // Fetch sport name
  const { data: sport } = useQuery({
    queryKey: ['sport', event?.sport_id],
    queryFn: async () => {
      if (!event?.sport_id) return null;
      const { data, error } = await supabase
        .from('sports')
        .select('name')
        .eq('id', event.sport_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!event?.sport_id
  });

  // Fetch age divisions from event_pricing JSONB
  const { data: ageDivisions } = useQuery({
    queryKey: ['age-divisions', eventId],
    queryFn: async () => {
      if (!eventId) return [];
      const { data, error } = await supabase
        .from('event_pricing')
        .select('pricing_structure')
        .eq('event_id', eventId)
        .maybeSingle();
      if (error) throw error;
      
      // Extract division_pricing from JSONB
      const pricingStructure = data?.pricing_structure || {};
      const divisions = pricingStructure.division_pricing || [];
      return divisions.map((div: any) => ({
        name: div.name,
        min_age: div.min_age,
        max_age: div.max_age
      }));
    },
    enabled: !!eventId
  });

  // Load full event data including related tables for form editing
  useEffect(() => {
    if (event && eventId) {
      loadFullEventData(eventId);
    }
  }, [event, eventId]);

  const loadFullEventData = async (id: string) => {
    try {
      // Fetch from new event_pricing JSONB
      const [
        { data: eventPricing },
        { data: notificationRecipients }
      ] = await Promise.all([
        supabase.from('event_pricing').select('pricing_structure').eq('event_id', id).maybeSingle(),
        supabase.from('event_notification_recipients').select('*').eq('event_id', id)
      ]);

      // Extract pricing data from JSONB structure
      const pricingStructure = eventPricing?.pricing_structure || {};
      const pricingTiers = pricingStructure.registration_tiers || [];
      const ageGroups = (pricingStructure.division_pricing || []).map((div: any) => ({
        id: div.id || crypto.randomUUID(),
        name: div.name,
        min_age: div.min_age,
        max_age: div.max_age,
        price: (div.price || 0) / 100,
        is_active: div.is_active ?? true
      }));
      const spectatorTickets = (pricingStructure.spectator_tickets || []).map((ticket: any) => ({
        ...ticket,
        price: (ticket.price || 0) / 100
      }));
      const eventExtras = (pricingStructure.extras || []).map((extra: any) => ({
        ...extra,
        price: (extra.price || 0) / 100
      }));

      const fullFormData: EventFormData = {
        id: event.id,
        name: event.name || '',
        description: event.description || '',
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        registration_open_date: event.registration_open_date || '',
        registration_close_date: event.registration_close_date || '',
        gender: event.gender || '',
        sport_id: event.sport_id || '',
        event_type_id: event.event_type_id || '',
        event_category_ids: event.event_category_ids || [],
        accept_online_registrations: event.accept_online_registrations || false,
        allow_checkin: event.allow_checkin || false,
        registration_type: event.registration_type || 'individual',
        allow_coupons: event.allow_coupons || false,
        discount_policy_id: event.discount_policy_id || '',
        external_registration_url: event.external_registration_url || '',
        venue_name: event.venue_name || '',
        venue_address: event.venue_address || '',
        venue_city: event.venue_city || '',
        venue_state: event.venue_state || '',
        venue_zip: event.venue_zip || '',
        venue_id: event.venue_id || '',
        main_venue_id: event.main_venue_id || '',
        additional_venue_ids: event.additional_venue_ids || [],
        registration_fee: event.registration_fee || 0,
        max_participants: event.max_participants || 0,
        roster_deadline: event.roster_deadline || '',
        gallery_images: event.gallery_images || [],
        event_format: event.event_format || '',
        checkin_instructions: event.check_in_instructions_html || '',
        rules: event.rules || '',
        schedule_info: event.schedule_info || '',
        pricing_notes: event.pricing_notes || '',
        age_groups_description: event.age_groups_description || '',
        hotel_booking_url: event.hotel_booking_url || '',
        promo_video_url: event.promo_video_url || '',
        live_streaming_description: event.live_streaming_description || '',
        live_streaming_url: event.live_streaming_url || '',
        enable_media_credentials: event.enable_media_credentials || false,
        media_credentials_description: event.media_credentials_description || '',
        media_credentials_form_id: event.media_credentials_form_id || '',
        media_credentials_notification_email: event.media_credentials_notification_email || '',
        media_credentials_price: event.media_credentials_price || 0,
        media_credentials_coupon_code: event.media_credentials_coupon_code || '',
        media_credentials_coupon_discount_type: event.media_credentials_coupon_discount_type || 'percentage',
        media_credentials_coupon_discount_value: event.media_credentials_coupon_discount_value || 100,
        coach_notification_email: event.coach_notification_email || '',
        coach_coupon_code: event.coach_coupon_code || '',
        enable_custom_form: event.enable_custom_form || false,
        custom_form_description: event.custom_form_description || '',
        custom_form_id: event.custom_form_id || '',
        display_format: event.display_format || '',
        rules_instructions: event.rules_instructions || '',
        selected_sponsor_ids: event.selected_sponsor_ids || [],
        new_sponsor: { name: '', website_url: '', logo_file: null },
        status: event.status || 'draft',
        thumbnail_url: event.thumbnail_url || null,
        background_image_url: event.background_image_url || null,
        accept_special_requests: event.accept_special_requests || false,
        special_request_instructions: event.special_request_instructions || '',
        special_request_fee: event.special_request_fee || 0,
        pricing_display_text: event.pricing_display_text || '',
        ticket_display_text: event.ticket_display_text || '',
        coaches_external_url: event.coaches_external_url || '',
        enable_coach_info: event.enable_coach_info || false,
        coach_info_description: event.coach_info_description || '',
        coach_info_form_id: event.coach_info_form_id || '',
        more_info: event.more_info || '',
        packet_enabled: event.packet_enabled || false,
        packet_price: (event.packet_price || 0) / 100,
        default_packet_template_id: event.default_packet_template_id || '',
        packet_default_age_groups: event.packet_default_age_groups || [],
        packet_include_all_age_groups: event.packet_include_all_age_groups ?? true,
        google_sheets_webhook_url: event.google_sheets_webhook_url || '',
        google_sheets_tab_name: event.google_sheets_tab_name || '',
        google_sheets_column_mapping: (event.google_sheets_column_mapping as Record<string, string>) || {},
        media_credentials_sheets_id: event.media_credentials_sheets_id || '',
        media_credentials_sheets_tab_name: event.media_credentials_sheets_tab_name || '',
        coach_attendance_google_sheet_url: event.coach_attendance_google_sheet_url || '',
        coach_attendance_sheets_tab_name: event.coach_attendance_sheets_tab_name || '',
        pricing_tiers: pricingTiers || [],
        age_groups: ageGroups || [],
        spectator_tickets: spectatorTickets || [],
        event_extras: eventExtras || [],
        notification_recipients: notificationRecipients || []
      };

      setFormData(fullFormData);
    } catch (error) {
      console.error('Error loading full event data:', error);
    }
  };

  const updateFormData = (data: Partial<EventFormData>) => {
    if (!formData) return;
    setFormData({ ...formData, ...data });
  };

  const handleSaveEvent = async () => {
    if (!formData || !eventId) return;
    
    setSaving(true);
    try {
      // Helper function to clean UUID fields
      const cleanUUID = (value: string | undefined | null): string | null => {
        if (!value || value.trim() === '') return null;
        return value;
      };

      // Helper function to clean date fields
      const cleanDate = (value: string | undefined | null): string | null => {
        if (!value || value.trim() === '') return null;
        return value;
      };

      // Create the event data object
      const eventData = {
        name: formData.name,
        description: formData.description,
        start_date: cleanDate(formData.start_date),
        end_date: cleanDate(formData.end_date),
        event_type_id: cleanUUID(formData.event_type_id),
        registration_type: formData.registration_type,
        additional_venue_ids: formData.additional_venue_ids || [],
        is_active: true,
        accept_online_registrations: formData.accept_online_registrations,
        external_registration_url: formData.external_registration_url || null,
        discount_policy_id: cleanUUID(formData.discount_policy_id),
        roster_deadline: formData.registration_type === 'team' ? cleanDate(formData.roster_deadline) : null,
        promo_video_url: formData.promo_video_url || null,
        gallery_images: formData.gallery_images || [],
        registration_open_date: cleanDate(formData.registration_open_date),
        registration_close_date: cleanDate(formData.registration_close_date),
        event_category_ids: formData.event_category_ids || [],
        registration_alert_emails: formData.notification_recipients?.map(r => r.email) || [],
        payment_notification_emails: formData.notification_recipients?.map(r => r.email) || [],
        roster_upload_emails: formData.notification_recipients?.map(r => r.email) || [],
        media_credentials_description: formData.media_credentials_description || null,
        media_credentials_form_id: cleanUUID(formData.media_credentials_form_id),
        media_credentials_notification_email: formData.media_credentials_notification_email || null,
        media_credentials_coupon_code: formData.media_credentials_coupon_code || null,
        media_credentials_coupon_discount_type: formData.media_credentials_coupon_discount_type || 'percentage',
        media_credentials_coupon_discount_value: formData.media_credentials_coupon_discount_value || 100,
        coach_notification_email: formData.coach_notification_email || null,
        coach_coupon_code: formData.coach_coupon_code || null,
        live_streaming_description: formData.live_streaming_description || null,
        live_streaming_url: formData.live_streaming_url || null,
        allow_checkin: formData.allow_checkin,
        status: formData.status,
        allow_coupons: formData.allow_coupons,
        venue_address: formData.venue_address || null,
        venue_city: formData.venue_city || null,
        venue_state: formData.venue_state || null,
        venue_zip: formData.venue_zip || null,
        venue_name: formData.venue_name || null,
        venue_id: cleanUUID(formData.venue_id),
        main_venue_id: cleanUUID(formData.main_venue_id),
        check_in_instructions_html: formData.checkin_instructions || null,
        display_format: formData.display_format || null,
        enable_media_credentials: formData.enable_media_credentials || false,
        media_credentials_price: formData.media_credentials_price || 0,
        event_format: formData.event_format || null,
        hotel_booking_url: formData.hotel_booking_url || null,
        pricing_notes: formData.pricing_notes || null,
        rules: formData.rules || null,
        schedule_info: formData.schedule_info || null,
        age_groups_description: formData.age_groups_description || null,
        purchasable_packet_url: formData.purchasable_packet_url || null,
        selected_sponsor_ids: formData.selected_sponsor_ids || [],
        is_archived: false,
        deleted_at: null,
        accept_special_requests: formData.accept_special_requests || false,
        special_request_instructions: formData.accept_special_requests ? formData.special_request_instructions : null,
        special_request_fee: formData.accept_special_requests ? formData.special_request_fee : null,
        thumbnail_url: formData.thumbnail_url || null,
        background_image_url: formData.background_image_url || null,
        gender: formData.gender || null,
        sport_id: cleanUUID(formData.sport_id),
        enable_custom_form: formData.enable_custom_form || false,
        custom_form_description: formData.custom_form_description || null,
        custom_form_id: cleanUUID(formData.custom_form_id),
        enable_coach_info: formData.enable_coach_info || false,
        coach_info_description: formData.coach_info_description || null,
        coach_info_form_id: cleanUUID(formData.coach_info_form_id),
        ticket_display_text: formData.ticket_display_text || null,
        coaches_external_url: formData.coaches_external_url || null,
        more_info: formData.more_info || null,
        packet_enabled: formData.packet_enabled || false,
        packet_price: Math.round((formData.packet_price || 0) * 100),
        default_packet_template_id: cleanUUID(formData.default_packet_template_id),
        packet_default_age_groups: formData.packet_default_age_groups || [],
        packet_include_all_age_groups: formData.packet_include_all_age_groups ?? true,
        google_sheets_webhook_url: formData.google_sheets_webhook_url || null,
        google_sheets_tab_name: formData.google_sheets_tab_name || null,
        google_sheets_column_mapping: formData.google_sheets_column_mapping || null,
        media_credentials_sheets_id: formData.media_credentials_sheets_id || null,
        media_credentials_sheets_tab_name: formData.media_credentials_sheets_tab_name || null,
        coach_attendance_google_sheet_url: formData.coach_attendance_google_sheet_url || null,
        coach_attendance_sheets_tab_name: formData.coach_attendance_sheets_tab_name || null
      };

      // Update the event
      const { error: eventError } = await supabase
        .from('events')
        .update(eventData)
        .eq('id', eventId);

      if (eventError) {
        console.error('Error updating event:', eventError);
        toast.error(`Failed to save event: ${eventError.message}`);
        return;
      }

      // Handle event categories
      if (formData.event_category_ids) {
        await supabase
          .from('event_category_mappings')
          .delete()
          .eq('event_id', eventId);

        if (formData.event_category_ids.length > 0) {
          const categoryMappings = formData.event_category_ids.map(categoryId => ({
            event_id: eventId,
            event_category_id: categoryId
          }));

          await supabase
            .from('event_category_mappings')
            .insert(categoryMappings);
        }
      }

      toast.success('Event updated successfully!');
      refetch();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!eventId) return;
    
    const newStatus = event?.status === 'published' ? 'draft' : 'published';
    
    try {
      const { error } = await supabase
        .from('events')
        .update({ status: newStatus })
        .eq('id', eventId);

      if (error) throw error;
      
      toast.success(`Event ${newStatus === 'published' ? 'published' : 'unpublished'} successfully!`);
      refetch();
    } catch (error) {
      console.error('Error updating event status:', error);
      toast.error('Failed to update event status');
    }
  };

  const getEventStatus = (event: any) => {
    const now = new Date();
    const start = parseLocalDate(event.start_date);
    const end = parseLocalDate(event.end_date);
    
    if (now > end) return { label: 'Past', variant: 'secondary' as const };
    if (now < start && event.status === 'published') return { label: 'Upcoming', variant: 'default' as const };
    if (now >= start && now <= end) return { label: 'Live', variant: 'destructive' as const };
    if (event.status === 'draft') return { label: 'Draft', variant: 'outline' as const };
    return { label: 'Scheduled', variant: 'default' as const };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold mb-2">Event not found</h3>
        <p className="text-muted-foreground mb-4">
          The event you're looking for doesn't exist or has been removed.
        </p>
        <Button onClick={() => navigate('/admin-dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const status = getEventStatus(event);
  const location = [event.venue_city, event.venue_state].filter(Boolean).join(', ');

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin-dashboard')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        <div className="flex gap-2">
          <Button 
            variant={event.status === 'published' ? 'outline' : 'default'}
            onClick={handleToggleStatus}
          >
            <Globe className="h-4 w-4 mr-2" />
            {event.status === 'published' ? 'Unpublish' : 'Publish Event'}
          </Button>
          <Button 
            variant="outline"
            onClick={() => {
              if (!event.slug) {
                toast.error('Event must have a slug to view public page');
                return;
              }
              if (event.status !== 'published') {
                toast.info('This is a draft event - public page may not display correctly until published');
              }
              window.open(`/events/${event.slug}`, '_blank');
            }}
            disabled={!event.slug}
          >
            <Eye className="h-4 w-4 mr-2" />
            View Public Page
          </Button>
          <Button 
            onClick={handleSaveEvent}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save All Changes'
            )}
          </Button>
        </div>
      </div>

      {/* Event Info */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold">{event.name}</h1>
          <Badge variant={status.variant}>{status.label}</Badge>
          {event.status === 'published' ? (
            <Badge variant="outline">
              <Globe className="h-3 w-3 mr-1" />
              Published
            </Badge>
          ) : (
            <Badge variant="secondary">Draft</Badge>
          )}
        </div>
      </div>

      <Separator />

      {/* Tabbed Content */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-12 h-auto flex-wrap">
          <TabsTrigger value="overview" className="flex-shrink-0">
            <ListChecks className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="basic-info" className="flex-shrink-0">
            <Info className="h-4 w-4 mr-2" />
            Basic Info
          </TabsTrigger>
          <TabsTrigger value="venue" className="flex-shrink-0">
            <MapPinned className="h-4 w-4 mr-2" />
            Venue
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex-shrink-0">
            <DollarSign className="h-4 w-4 mr-2" />
            Pricing
          </TabsTrigger>
          <TabsTrigger value="details" className="flex-shrink-0">
            <FileText className="h-4 w-4 mr-2" />
            Details
          </TabsTrigger>
          <TabsTrigger value="sponsors" className="flex-shrink-0">
            <Trophy className="h-4 w-4 mr-2" />
            Sponsors
          </TabsTrigger>
          <TabsTrigger value="faqs" className="flex-shrink-0">
            <HelpCircle className="h-4 w-4 mr-2" />
            FAQs
          </TabsTrigger>
          <TabsTrigger value="coaches" className="flex-shrink-0">
            <UserCheck className="h-4 w-4 mr-2" />
            Coaches
          </TabsTrigger>
          <TabsTrigger value="registrations" className="flex-shrink-0">
            <Users className="h-4 w-4 mr-2" />
            Registrations
          </TabsTrigger>
          <TabsTrigger value="schedule-requests" className="flex-shrink-0">
            <ClipboardList className="h-4 w-4 mr-2" />
            Scheduling
          </TabsTrigger>
          <TabsTrigger value="finance" className="flex-shrink-0">
            <TrendingUp className="h-4 w-4 mr-2" />
            Finance
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex-shrink-0">
            <Plug className="h-4 w-4 mr-2" />
            Integrations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Registration Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium capitalize">
                    {event.registration_type || 'Individual'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Online Registration:</span>
                  <span className="font-medium">
                    {event.accept_online_registrations ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {event.registration_open_date && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Opens:</span>
                    <span className="font-medium">
                      {new Date(event.registration_open_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {event.registration_close_date && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Closes:</span>
                    <span className="font-medium">
                      {new Date(event.registration_close_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Event Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {event.gender && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gender:</span>
                    <span className="font-medium capitalize">{event.gender}</span>
                  </div>
                )}
                {sport && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sport:</span>
                    <span className="font-medium">{sport.name}</span>
                  </div>
                )}
                {ageDivisions && ageDivisions.length > 0 && (
                  <div>
                    <span className="text-muted-foreground block mb-2">Age Divisions:</span>
                    <div className="flex flex-wrap gap-2">
                      {ageDivisions.map((division, idx) => (
                        <Badge key={idx} variant="outline">
                          {division.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/admin/tournaments?eventId=${eventId}`)}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Tournament / Scheduler
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/admin/communications')}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Communications
                </Button>
              </div>
            </CardContent>
          </Card>

          {event.featured_image_url && (
            <Card>
              <CardHeader>
                <CardTitle>Featured Image</CardTitle>
              </CardHeader>
              <CardContent>
                <img 
                  src={event.featured_image_url} 
                  alt={event.name}
                  className="w-full max-w-2xl rounded-lg"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Basic Info Tab - from old system */}
        <TabsContent value="basic-info" className="mt-6">
          {formData && (
            <BasicInfoTab 
              formData={formData} 
              updateFormData={updateFormData} 
            />
          )}
        </TabsContent>

        {/* Venue/Location Tab - from old system */}
        <TabsContent value="venue" className="mt-6">
          {formData && (
            <VenueLocationTab 
              formData={formData} 
              updateFormData={updateFormData} 
            />
          )}
        </TabsContent>

        {/* NEW Pricing Config Tab - modular system */}
        <TabsContent value="pricing" className="mt-6">
          <EventPricingConfig />
        </TabsContent>

        {/* Details Tab - from old system */}
        <TabsContent value="details" className="mt-6">
          {formData && (
            <DetailsTab 
              formData={formData} 
              updateFormData={updateFormData} 
            />
          )}
        </TabsContent>

        {/* Sponsors Tab - from old system */}
        <TabsContent value="sponsors" className="mt-6">
          {formData && (
            <SponsorsTab 
              formData={formData} 
              updateFormData={updateFormData} 
            />
          )}
        </TabsContent>

        {/* FAQs Tab - from old system */}
        <TabsContent value="faqs" className="mt-6">
          <FAQsTab eventId={eventId} />
        </TabsContent>

        {/* Coaches Tab - from old system */}
        <TabsContent value="coaches" className="mt-6">
          {eventId && <CoachesTab eventId={eventId} />}
        </TabsContent>

        {/* NEW Registrations Tab - modular system */}
        <TabsContent value="registrations" className="mt-6">
          <EventRegistrationsAdmin />
        </TabsContent>

        {/* Schedule Requests Tab */}
        <TabsContent value="schedule-requests" className="mt-6">
          {eventId && event?.organization_id && (
            <EventScheduleRequestsTab eventId={eventId} organizationId={event.organization_id} />
          )}
        </TabsContent>

        {/* NEW Finance Tab */}
        <TabsContent value="finance" className="mt-6">
          <EventFinanceTab />
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="mt-6">
          {formData && (
            <IntegrationsTab 
              formData={formData} 
              updateFormData={updateFormData}
              eventId={eventId}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Default export without wrapper - will be wrapped by route
export default function EventManagement() {
  return <EventManagementContent />;
}
